import React, { useEffect, useRef } from "react";
import { Navigate, useParams } from "react-router-dom";
import type { CloudscapeJsonSchema } from "@amzn/fin-tech-cloudscape-json-forms";

import LoadingIndicator from "src/components/loadingIndicator/LoadingIndicator";
import { buildLobClaimIntakePath } from "src/jsonForms/constants";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import SchemaFormWithSubmission from "./SchemaFormWithSubmission";

import {
  fetchPreScreenSchema,
  resetPreScreen,
  setResolveSchemaInput,
  setResolvedSchemaName,
  setWcPreScreenData,
  type ResolveSchemaInput,
} from "./jsonFormsSlice";
import {
  getAvailableLobsForRoles,
  normalizeLobParam,
  userHasLobAccess,
} from "./preScreen/lobAccess";
import type { LobKey, PreScreenLobConfig } from "./preScreen/types";
import { LOB_CONFIGS } from "./preScreen/lobConfigs";
import type { AnyPreScreenLobConfig } from "./preScreen/lobConfigs";
import LobSelector from "./preScreen/LobSelector";
import AccessDeniedAlert from "./preScreen/AccessDeniedAlert";
import SchemaFetchError from "./preScreen/SchemaFetchError";
import { resolveWcTpa, toWcSchemaTpa } from "./preScreen/wcTpaResolution";
import { getSite } from "src/sites/sitesSlice";
import { SiteTypes } from "src/sites/constants";

/**
 * Maps a `lineOfBusiness` value from a persisted `ResolveSchemaInput` back
 * to the corresponding `LobKey`. Returns `null` when the input is missing
 * or contains an unrecognized LOB — callers must treat that as "no
 * persisted LOB to clear".
 */
function inferLobFromResolveInput(input: ResolveSchemaInput | null): LobKey | null {
  if (!input) {
    return null;
  }
  switch (input.lineOfBusiness) {
    case "workers-compensation":
      return "wc";
    case "cargo":
      return "cargo";
    case "product-liability":
      return "ipl";
    case "atoz":
      return "atoz";
    default:
      return null;
  }
}

interface PreScreenSubmitterProps {
  config: AnyPreScreenLobConfig;
  dispatchFetch: (input: ResolveSchemaInput) => void;
}

/**
 * Wraps a single LOB's `PreScreenComponent` with the LOB-agnostic
 * submit handler that drives the schema fetch.
 *
 * On submit:
 * 1. Build the resolve-schema input. If the builder returns `null` (for
 *    example when the WC resolver returns `UNSUPPORTED_TPA` or feature
 *    flags are still loading), do nothing — the LOB pre-screen surfaces
 *    its own alert.
 * 2. Persist the captured pre-screen data via the LOB's `saveAction`.
 * 3. Persist the resolve-schema input so a refresh can re-fetch it.
 * 4. Dispatch `fetchPreScreenSchema` to transition the slice into the
 *    `loading` schema-fetch state.
 */
function PreScreenSubmitter({ config, dispatchFetch }: PreScreenSubmitterProps): JSX.Element {
  const dispatch = useAppDispatch();

  // TypeScript cannot narrow a generic union (`AnyPreScreenLobConfig`)
  // at the value level. The cast to `PreScreenLobConfig<unknown>` is
  // safe here because `config` always matches the `data` shape produced
  // by its own `PreScreenComponent` — the generic is erased at runtime.
  const handleSubmit = (data: unknown) => {
    const { buildResolveSchemaInput, saveAction } = config as PreScreenLobConfig<unknown>;
    const input = buildResolveSchemaInput(data);
    if (!input) {
      return;
    }
    dispatch(saveAction(data));
    dispatch(setResolveSchemaInput(input));
    dispatchFetch(input);
  };

  const PreScreenComponent = config.PreScreenComponent as React.ComponentType<{
    onPreScreenSubmit: (data: unknown) => void;
  }>;
  return <PreScreenComponent onPreScreenSubmit={handleSubmit} />;
}

interface SchemaFetchLifecycleProps {
  status: "idle" | "loading" | "success" | "error";
  resolveSchemaInput: ResolveSchemaInput | null;
  resolvedSchemaId: string | null;
  errorMessage: string | null;
  fetchedSchema: CloudscapeJsonSchema | null;
  preScreenClaimContext: Record<string, unknown>;
}

/**
 * Renders the schema-fetch lifecycle UI (spinner / error / success)
 * once a pre-screen has been submitted and the slice transitions out
 * of `idle`. Returns `null` when the status is `idle` (or `success`
 * without a schema) so the caller can fall through to render the
 * pre-screen component instead.
 *
 * Extracted from the orchestrator to keep its branching complexity
 * under the project's eslint threshold without sacrificing the
 * explicit dispatch-table style.
 */
function renderSchemaFetchLifecycle({
  status,
  resolveSchemaInput,
  resolvedSchemaId,
  errorMessage,
  fetchedSchema,
  preScreenClaimContext,
}: SchemaFetchLifecycleProps): JSX.Element | null {
  if (status === "loading") {
    return <LoadingIndicator label="Loading form..." testId="schema-fetch-spinner" />;
  }
  if (status === "error") {
    const displayName = resolveSchemaInput
      ? `${resolveSchemaInput.lineOfBusiness}/${resolveSchemaInput.stage ?? "register"}`
      : "";
    return <SchemaFetchError resolvedSchemaName={displayName} errorMessage={errorMessage} />;
  }
  if (status === "success" && fetchedSchema) {
    return (
      <SchemaFormWithSubmission
        fetchedSchema={fetchedSchema}
        resolvedSchemaName={resolvedSchemaId}
        preScreenClaimContext={preScreenClaimContext}
      />
    );
  }
  return null;
}

/**
 * `JsonFormsClaimIntakeApp` — the LOB-agnostic orchestrator that owns the
 * pre-screening flow. The same component mounts at `/json-forms` and
 * at `/json-forms/<lob>/claim-intake-form`; branching is driven by
 * URL params and the user's roles.
 *
 * Render branching, in order:
 * 1. Auth loading guard.
 * 2. No LOB roles at all → access-denied alert.
 * 3. URL has an unknown `<lob>` parameter → access-denied alert.
 * 4. No `<lob>` and exactly one available LOB → redirect to that LOB.
 * 5. No `<lob>` and multiple available LOBs → LOB selector.
 * 6. `<lob>` user has no access to → access-denied alert.
 * 7. Schema-fetch lifecycle (loading / error / success) for the
 *    matched LOB once a pre-screen has been submitted.
 * 8. Otherwise → render the LOB's pre-screen component.
 *
 * On mount the shell also runs a single rehydration `useEffect` that
 * reconciles persisted pre-screen state with the current user's access
 * before any schema fetch is dispatched.
 */
function JsonFormsClaimIntakeApp(): JSX.Element {
  const { lob: urlLob } = useParams<{ lob?: string }>();
  const dispatch = useAppDispatch();
  const { user, authenticating } = useAppSelector((state) => state.auth);
  const preScreen = useAppSelector((state) => state.jsonForms.preScreen);
  const incidentSiteFromState = useAppSelector((state) => state.sites.incidentSite);

  // Tracks whether the rehydration `useEffect` has already executed
  // for this mount. Without the ref, an auth-state change later in the
  // session (for example a user-object refresh) would re-clear pre-screen
  // state and re-fire the schema fetch.
  const rehydratedRef = useRef(false);

  // Holds the abort handle for the in-flight schema fetch so a new
  // submission can cancel the previous request and prevent cross-LOB
  // state corruption from a stale fulfillment.
  //
  // NOTE: We intentionally do NOT abort on unmount. If the user
  // navigates away mid-fetch, the request completes and updates Redux
  // harmlessly. Aborting on unmount would reset `schemaFetchStatus` to
  // `idle` (via the AbortError handler in the slice), which causes the
  // rehydration effect on the next mount to re-fetch — discarding the
  // already-resolved schema and kicking the user back to the beginning
  // of the pre-screen flow.
  const fetchAbortRef = useRef<{ abort: () => void } | null>(null);

  /** Abort any in-flight fetch and dispatch a new one. */
  const dispatchFetch = (input: ResolveSchemaInput) => {
    fetchAbortRef.current?.abort();
    const promise = dispatch(fetchPreScreenSchema(input));
    fetchAbortRef.current = promise;
  };

  // Single-fire rehydration after auth loads. Must run before any
  // render branch that depends on the persisted pre-screen state so
  // that state-clear decisions happen before the user sees stale UI.
  useEffect(() => {
    if (authenticating || rehydratedRef.current) {
      return;
    }
    rehydratedRef.current = true;

    // If the last session ended with a successful submission, clear the
    // entire pre-screen so the user starts fresh from the beginning.
    if (preScreen.submissionCompleted) {
      dispatch(resetPreScreen());
      return;
    }

    const persistedLob = inferLobFromResolveInput(preScreen.resolveSchemaInput);

    // When the user no longer has access to the LOB whose pre-screen
    // produced the persisted resolve input, clear the entire pre-screen
    // sub-state and skip the schema fetch. Without this guard a user
    // who lost access between sessions would still see the cached
    // form and trigger an unauthorized fetch.
    if (persistedLob && !userHasLobAccess(user.roles, persistedLob)) {
      dispatch(resetPreScreen());
      return;
    }

    // Persisted-WC-TPA-mismatch check. The `incidentSiteCode` is the
    // only piece of incident-site context that survives a page reload
    // (country and state are not persisted to keep the localStorage
    // payload small and avoid drift from the canonical site config).
    // We re-fetch the site so the resolver has fresh country/state,
    // but we cannot await it inside the rehydration effect — the
    // resolver runs against `state.sites.incidentSite` synchronously.
    //
    // Approach: dispatch `getSite` to populate `state.sites`, then
    // synchronously check the resolver against the cached site if
    // present; if the resolver disagrees with the persisted TPA, clear
    // the WC pre-screen so the user re-runs the flow. If the cached
    // site is missing the re-fetch covers the next render — when the
    // resolver finally has the new context, the user resubmits.
    if (preScreen.wc) {
      const persistedSiteCode = preScreen.wc.incidentSiteCode;
      if (persistedSiteCode) {
        dispatch(
          getSite({
            code: persistedSiteCode,
            type: SiteTypes.INCIDENT_SITE,
          })
        );
      }
      const cachedIncidentSite =
        persistedSiteCode && incidentSiteFromState?.code === persistedSiteCode
          ? incidentSiteFromState
          : null;
      const expectedSchemaTpa = toWcSchemaTpa(
        resolveWcTpa({
          country: cachedIncidentSite?.country,
          state: cachedIncidentSite?.state,
          incidentDate: preScreen.wc.incidentDate,
        })
      );
      if (expectedSchemaTpa && expectedSchemaTpa !== preScreen.wc.resolvedTpa) {
        dispatch(setWcPreScreenData(null));
        dispatch(setResolveSchemaInput(null));
        dispatch(setResolvedSchemaName(null));
        return;
      }
    }

    // Re-dispatch the schema fetch when an input is persisted but the
    // (non-persisted) `fetchedSchema` was not rehydrated. The status
    // guard mirrors the persistence contract — on a real page reload
    // `schemaFetchStatus` resets to `idle` (it is not persisted, see
    // `preScreenPersistence`), so a non-`idle` status here means the
    // user has just submitted or hit retry within this session and the
    // hook MUST NOT clobber that in-flight or terminal state.
    if (
      preScreen.resolveSchemaInput &&
      !preScreen.fetchedSchema &&
      preScreen.schemaFetchStatus === "idle"
    ) {
      dispatchFetch(preScreen.resolveSchemaInput);
    }
    // The dependency array intentionally only listens to `authenticating`
    // so the hook fires exactly once after auth becomes ready. The
    // `rehydratedRef` guard above prevents accidental re-runs.
    // eslint-disable-next-line
  }, [authenticating]);

  if (authenticating) {
    return <LoadingIndicator label="Loading..." testId="auth-loading-spinner" />;
  }

  const availableLobs = getAvailableLobsForRoles(user.roles);
  const lobKey = normalizeLobParam(urlLob);

  if (availableLobs.length === 0) {
    return <AccessDeniedAlert />;
  }

  // Unknown `<lob>` param: render access-denied with a link back to
  // the LOB selector since the user does have at least one LOB.
  if (urlLob && lobKey === null) {
    return <AccessDeniedAlert hasOtherLobs />;
  }

  if (!lobKey && availableLobs.length === 1) {
    return <Navigate to={buildLobClaimIntakePath(availableLobs[0])} replace />;
  }

  if (!lobKey && availableLobs.length > 1) {
    return <LobSelector available={availableLobs} />;
  }

  if (lobKey && !availableLobs.includes(lobKey)) {
    return <AccessDeniedAlert hasOtherLobs={availableLobs.length > 0} />;
  }

  // From here on `lobKey` is non-null and the user has access to it.
  const config = LOB_CONFIGS[lobKey!];

  // Surface the schema-fetch lifecycle ahead of the pre-screen so the
  // user does not see the form again after submitting. The lifecycle
  // is gated on the persisted resolve input belonging to the LOB the
  // user is currently on — without this gate, an error from a
  // previous LOB submission would render on every other LOB the user
  // navigates to (the slice keys schemaFetchStatus/resolveSchemaInput
  // are single-valued and shared across all LOBs by design). When the
  // helper returns `null` (status `idle`, no persisted input, input
  // belongs to a different LOB, or `success` with no schema yet) the
  // orchestrator falls through to the pre-screen.
  const persistedLifecycleLob = inferLobFromResolveInput(preScreen.resolveSchemaInput);
  const lifecycleBelongsToCurrentLob =
    persistedLifecycleLob !== null && persistedLifecycleLob === lobKey;
  // Resolve the pre-screen selections that must travel with the claim
  // payload from the LOB's own `buildClaimContext`. The cast to
  // `PreScreenLobConfig<unknown>` mirrors `PreScreenSubmitter` — the
  // generic is erased at runtime and `preScreen[lobKey]` always matches
  // the config's `TData`. Falls back to `{}` when no data is persisted.
  const preScreenData = preScreen[lobKey!];
  const preScreenClaimContext = preScreenData
    ? (config as PreScreenLobConfig<unknown>).buildClaimContext(preScreenData)
    : {};
  const lifecycleElement = lifecycleBelongsToCurrentLob
    ? renderSchemaFetchLifecycle({
        status: preScreen.schemaFetchStatus,
        resolveSchemaInput: preScreen.resolveSchemaInput,
        resolvedSchemaId: preScreen.resolvedSchemaId,
        errorMessage: preScreen.schemaFetchErrorMessage,
        fetchedSchema: preScreen.fetchedSchema,
        preScreenClaimContext,
      })
    : null;
  if (lifecycleElement) {
    return lifecycleElement;
  }

  return <PreScreenSubmitter config={config} dispatchFetch={dispatchFetch} />;
}

export default JsonFormsClaimIntakeApp;
